export interface PlugPort {
  portId: number;
  status: number;
  displayName: string;
  lastTimeUsed: string;
}

export interface Plug {
  Code: number;
  Name: string;
  Count: number;
  Level: number;
  Status?: number;
  LastTimeUsed?: string;
  Network?: number;
  NetworkName?: string;
  ChargingRateUnit?: number;
  ChargingRateInfo?: string | null;
  ChargingRate?: number;
  ParkingRate?: number | null;
  ParkingUnit?: string;
  ParkingInfo?: string | null;
  Amp?: string;
  Kw?: string;
  Volt?: string;
  LiveStatusLastUpdate?: string;
  IsPassportEnabled?: boolean;
  FlexFee?: number;
  Ports?: PlugPort[];
  DetailedStatusInfo?: {
    String: string;
    Style: string;
  };
  // Legacy mock-data field, kept optional for backwards compatibility
  PriceString?: string;
}

export interface Station {
  Id: number;
  IdAsString?: string;
  IsShared?: boolean;
  LocName: string;
  Address: string;
  StreetNo?: number;
  Street?: string;
  City?: string;
  prov_state?: string;
  prov_state_FullName?: string;
  Country?: string;
  Country_FullName?: string;
  Zip?: string;
  Lat: number;
  Long: number;
  Phone: string;
  Web?: string;
  AccessId?: number;
  AccessTime: string;
  AccessOtherDesc: string | null;
  TotalPorts: number;
  AvailablePorts: number;
  NetworkId?: number;
  NetworkName?: string;
  PriceNumber?: number;
  PriceString?: string;
  PriceStringInfo?: string;
  PriceStringSum?: string;
  LocDesc?: string | null;
  AccessType: string;
  StatusTime: string | null;
  PaymentMethods: string;
  IsPassportEnabled?: boolean;
  LocScore?: number | null;
  SemaStationArray?: unknown[];
  PlugsArray: Plug[];
}

export interface ToolData {
  tool_name: string;
  step_id: string;
  data: {
    status: string;
    total_requested: number;
    total_returned: number;
    stations: Station[];
  };
  count: number;
}

export interface RouteExtraction {
  origin?: string;
  destination?: string;
  corridor?: string;
  [key: string]: unknown;
}

export interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
  isError?: boolean;
  tool_data?: ToolData[];
  stations?: Station[];
  encodedPolyline?: string;
  extraction?: { route?: RouteExtraction; [key: string]: unknown };
  [key: string]: unknown;
}

// Shape of a single exchange as stored in Cosmos: one query/response pair per record.
export interface SessionMessageRecord {
  msgId: string;
  query: string;
  response: string;
  timestamp: string;
  extraction?: Record<string, unknown>;
  route?: {
    encodedPolyline?: string;
    [key: string]: unknown;
  };
  stations?: Station[];
  usage?: Record<string, unknown>;
}

export enum StreamEventType {
  NODE_START = 'node_start',
  NODE_COMPLETE = 'node_complete',
  TOOL_START = 'tool_start',
  TOOL_COMPLETE = 'tool_complete',
  STREAM_CHUNK = 'stream_chunk',
  WORKFLOW_COMPLETE = 'workflow_complete',
  ERROR = 'error',
}

export interface StreamEvent {
  type: StreamEventType;
  timestamp: string;
  nodeName?: string;
  toolName?: string;
  stepId?: string;
  content?: string;
  isComplete?: boolean;
  success?: boolean;
  summary?: string;
  details?: string;
  duration?: number;
  response?: string;
  extraction?: { route?: RouteExtraction; [key: string]: unknown };
  toolData?: Array<{
    stepId: string;
    toolName: string;
    data: {
      status: string;
      total_requested: number;
      total_returned: number;
      stations: Station[];
    };
    count: number;
    metadata?: unknown;
  }>;
  encodedPolyline?: string;
  route?: {
    encodedPolyline?: string;
    [key: string]: unknown;
  };
}

export interface StreamingStatus {
  currentStep: string;
  isStreaming: boolean;
  streamedContent: string;
}

export interface Session {
  id: string;
  title: string;
  messages: Message[];
  createdAt: string;
}

export interface Location {
  latitude: number;
  longitude: number;
  name?: string | null;
}

export interface SendMessageParams {
  query: string;
  sessionId: string;
  email: string;
  location?: Location | null;
}

export interface StationQueryResponse {
  success: boolean;
  status_code: number;
  message: string;
  data: {
    response: string;
    extraction: Record<string, unknown>;
    tools_used: number;
    session_id: string;
    tool_data: ToolData[];
  };
  error: string | null;
}
